import { EMPTY_INPUT, type InputState, type VehicleBlueprint } from "@neon-arena/shared";
import type { VehicleController } from "../physics/VehicleController.js";

export type TestPhase =
  | "idle"
  | "speed"
  | "corner_warmup"
  | "corner_turn"
  | "drift_warmup"
  | "drift_slide"
  | "boost"
  | "combat"
  | "done";

export type SpeedSubstate = "accelerating" | "braking" | "stopped";

export interface SpeedResult {
  id: string; name: string;
  topSpeed: number;
  timeToHalfMaxS: number | null;
  accelTimeToTargetS: number;   // time to reach 95% maxSpeed (or 10 s if timed out)
  reachedTopSpeed: boolean;     // false if timed out before reaching 95%
  accelDistanceM: number;       // distance covered during accel substate
  brakeDistanceM: number;       // distance covered during brake substate
  brakeTimeS: number;           // time spent braking before stopping
}

export interface CornerResult {
  id: string; name: string;
  headingChangeDeg: number;
  pathRadiusM: number | null;
  entrySpeed: number;
}

export interface DriftResult {
  id: string; name: string;
  lateralDisplacementM: number;
  headingChangeDeg: number;
  entrySpeed: number;
}

export interface CombatResult {
  id: string; name: string;
  damagePerShot: number;
  reloadMs: number;
  projectileSpeed: number;
  shotsFired: number;
  shotsHit: number;
  timeToKillS: number | null;
  dps: number;
}

export interface TestResults {
  timestamp: string;
  blueprintIds: string[];
  speed: SpeedResult[];
  corner: CornerResult[];
  drift: DriftResult[];
  combat: CombatResult[];
}

interface VehicleState {
  blueprint: VehicleBlueprint;
  controller: VehicleController;
  spawnX: number;
  spawnZ: number;
  // speed test
  speedSubstate: SpeedSubstate;
  topSpeed: number;
  halfMaxTime: number | null;
  accelEndTime: number | null;     // null while accelerating
  accelEndX: number;
  accelEndZ: number;
  reachedTopSpeed: boolean;
  brakeStopTime: number | null;
  // corner
  cornerStartX: number;
  cornerStartZ: number;
  cornerStartRotY: number;
  cornerEntrySpeed: number;
  cornerEndX: number;
  cornerEndZ: number;
  cornerEndRotY: number;
  // drift
  driftStartX: number;
  driftStartZ: number;
  driftStartRotY: number;
  driftEntrySpeed: number;
}

const PHASE_DURATIONS: Partial<Record<TestPhase, number>> = {
  speed: 25,             // grand timeout — phase ends earlier when all stopped
  corner_warmup: 4,
  corner_turn: 5,
  drift_warmup: 4,
  drift_slide: 4,
  boost: 8,
  combat: 20,
};

const SPEED_ACCEL_TIMEOUT = 10;     // each car switches to brake after 10 s if 95% not reached
const SPEED_TARGET_RATIO = 0.95;    // flip-to-brake threshold

const PAUSE_AFTER = new Set<TestPhase>(["speed", "corner_turn", "drift_slide", "combat"]);

const PHASE_ORDER: TestPhase[] = [
  "idle",
  "speed",
  "corner_warmup",
  "corner_turn",
  "drift_warmup",
  "drift_slide",
  "boost",
  "combat",
  "done",
];

export class TestRunner {
  phase: TestPhase = "idle";
  elapsed = 0;
  waitingForInput = false;
  private phaseChanged = false;
  private states: VehicleState[] = [];
  private results: Partial<TestResults> = {};

  init(blueprints: VehicleBlueprint[], controllers: VehicleController[]): void {
    this.states = blueprints.map((bp, i) => ({
      blueprint: bp,
      controller: controllers[i],
      spawnX: controllers[i].x,
      spawnZ: controllers[i].z,
      speedSubstate: "accelerating" as SpeedSubstate,
      topSpeed: 0,
      halfMaxTime: null,
      accelEndTime: null,
      accelEndX: 0, accelEndZ: 0,
      reachedTopSpeed: false,
      brakeStopTime: null,
      cornerStartX: 0, cornerStartZ: 0, cornerStartRotY: 0, cornerEntrySpeed: 0,
      cornerEndX: 0, cornerEndZ: 0, cornerEndRotY: 0,
      driftStartX: 0, driftStartZ: 0, driftStartRotY: 0, driftEntrySpeed: 0,
    }));
    this.results = { timestamp: "", blueprintIds: blueprints.map((b) => b.id) };
  }

  beginPhase(phase: TestPhase): void {
    this.phase = phase;
    this.elapsed = 0;
    this.waitingForInput = false;
    this.phaseChanged = true;

    for (const s of this.states) {
      const snap = s.controller.snapshot();
      if (phase === "speed") {
        s.speedSubstate = "accelerating";
        s.topSpeed = 0;
        s.halfMaxTime = null;
        s.accelEndTime = null;
        s.reachedTopSpeed = false;
        s.brakeStopTime = null;
      } else if (phase === "corner_turn") {
        s.cornerStartX = snap.x;
        s.cornerStartZ = snap.z;
        s.cornerStartRotY = snap.rotY;
        s.cornerEntrySpeed = snap.speed;
        s.cornerEndX = snap.x;
        s.cornerEndZ = snap.z;
        s.cornerEndRotY = snap.rotY;
      } else if (phase === "drift_slide") {
        s.driftStartX = snap.x;
        s.driftStartZ = snap.z;
        s.driftStartRotY = snap.rotY;
        s.driftEntrySpeed = snap.speed;
      }
    }
  }

  tick(dt: number): InputState[] {
    this.phaseChanged = false;
    if (this.phase === "idle" || this.phase === "done" || this.waitingForInput) {
      return this.states.map(() => ({ ...EMPTY_INPUT }));
    }

    this.elapsed += dt;
    const inputs: InputState[] = [];

    for (const s of this.states) {
      const snap = s.controller.snapshot();

      if (this.phase === "speed") {
        if (snap.speed > s.topSpeed) s.topSpeed = snap.speed;
        if (s.halfMaxTime === null && snap.speed >= s.blueprint.handling.maxSpeed * 0.5) {
          s.halfMaxTime = this.elapsed;
        }

        if (s.speedSubstate === "accelerating") {
          const target = s.blueprint.handling.maxSpeed * SPEED_TARGET_RATIO;
          if (snap.speed >= target) {
            s.speedSubstate = "braking";
            s.accelEndTime = this.elapsed;
            s.accelEndX = snap.x;
            s.accelEndZ = snap.z;
            s.reachedTopSpeed = true;
          } else if (this.elapsed >= SPEED_ACCEL_TIMEOUT) {
            s.speedSubstate = "braking";
            s.accelEndTime = this.elapsed;
            s.accelEndX = snap.x;
            s.accelEndZ = snap.z;
            s.reachedTopSpeed = false;
          }
        }

        if (s.speedSubstate === "braking" && Math.abs(snap.speed) < 0.15) {
          s.speedSubstate = "stopped";
          s.brakeStopTime = this.elapsed;
        }

        // input determined by current substate
        if (s.speedSubstate === "accelerating") {
          inputs.push({ ...EMPTY_INPUT, throttle: 1 });
        } else if (s.speedSubstate === "braking") {
          inputs.push({ ...EMPTY_INPUT, brake: 1 });
        } else {
          inputs.push({ ...EMPTY_INPUT });
        }
      } else if (this.phase === "corner_turn") {
        s.cornerEndX = snap.x;
        s.cornerEndZ = snap.z;
        s.cornerEndRotY = snap.rotY;
        inputs.push(this.inputForPhase());
      } else {
        inputs.push(this.inputForPhase());
      }
    }

    // speed phase: end when all cars stopped or grand timeout
    if (this.phase === "speed") {
      const allStopped = this.states.every((s) => s.speedSubstate === "stopped");
      const timeout = this.elapsed >= (PHASE_DURATIONS.speed ?? 25);
      if (allStopped || timeout) {
        this.finishPhase();
        this.afterPhase();
        return inputs;
      }
    }

    // generic timed phases
    const dur = PHASE_DURATIONS[this.phase];
    if (dur !== undefined && this.elapsed >= dur && this.phase !== "speed") {
      this.finishPhase();
      this.afterPhase();
    }

    return inputs;
  }

  continueFromWait(): void {
    if (this.waitingForInput) {
      this.waitingForInput = false;
      this.advancePhase();
    }
  }

  nextPhase(): void {
    if (this.waitingForInput) { this.continueFromWait(); return; }
    this.finishPhase();
    this.afterPhase();
  }

  private inputForPhase(): InputState {
    switch (this.phase) {
      case "corner_warmup": return { ...EMPTY_INPUT, throttle: 1 };
      case "corner_turn":   return { ...EMPTY_INPUT, throttle: 0.6, steer: 1.0 };
      case "drift_warmup":  return { ...EMPTY_INPUT, throttle: 1 };
      case "drift_slide":   return { ...EMPTY_INPUT, throttle: 0.55, steer: 0.85, handbrake: true };
      case "boost":         return { ...EMPTY_INPUT, throttle: 1, boost: true };
      case "combat":        return { ...EMPTY_INPUT };
      default: return { ...EMPTY_INPUT };
    }
  }

  private afterPhase(): void {
    if (PAUSE_AFTER.has(this.phase)) {
      this.waitingForInput = true;
      this.phaseChanged = true;
    } else {
      this.advancePhase();
    }
  }

  private advancePhase(): void {
    const idx = PHASE_ORDER.indexOf(this.phase);
    const next = PHASE_ORDER[idx + 1] ?? "done";
    if (next === "done") {
      this.phase = "done";
      this.phaseChanged = true;
      this.results.timestamp = new Date().toISOString();
      return;
    }
    this.beginPhase(next);
  }

  private finishPhase(): void {
    if (this.phase === "speed") {
      this.results.speed = this.states.map((s) => {
        const accelEndT = s.accelEndTime ?? this.elapsed;
        const stopT = s.brakeStopTime ?? this.elapsed;
        const accelDist = (s.accelEndZ - s.spawnZ);  // forward only (cars face +Z)
        const snap = s.controller.snapshot();
        const brakeDist = s.accelEndTime !== null ? (snap.z - s.accelEndZ) : 0;
        return {
          id: s.blueprint.id,
          name: s.blueprint.name,
          topSpeed: s.topSpeed,
          timeToHalfMaxS: s.halfMaxTime,
          accelTimeToTargetS: accelEndT,
          reachedTopSpeed: s.reachedTopSpeed,
          accelDistanceM: accelDist,
          brakeDistanceM: brakeDist,
          brakeTimeS: s.brakeStopTime !== null ? (stopT - accelEndT) : (this.elapsed - accelEndT),
        };
      });
    } else if (this.phase === "corner_turn") {
      this.results.corner = this.states.map((s) => {
        let diff = s.cornerEndRotY - s.cornerStartRotY;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        const headingDeg = Math.abs(diff) * (180 / Math.PI);
        const dx = s.cornerEndX - s.cornerStartX;
        const dz = s.cornerEndZ - s.cornerStartZ;
        const arcLen = Math.sqrt(dx * dx + dz * dz);
        const angleRad = Math.abs(diff);
        return {
          id: s.blueprint.id,
          name: s.blueprint.name,
          headingChangeDeg: headingDeg,
          pathRadiusM: angleRad > 0.05 ? arcLen / angleRad : null,
          entrySpeed: s.cornerEntrySpeed,
        };
      });
    } else if (this.phase === "drift_slide") {
      this.results.drift = this.states.map((s) => {
        const snap = s.controller.snapshot();
        const dx = snap.x - s.driftStartX;
        const dz = snap.z - s.driftStartZ;
        const sinH = Math.sin(s.driftStartRotY);
        const cosH = Math.cos(s.driftStartRotY);
        const lateral = Math.abs(dx * cosH - dz * sinH);
        let diff = snap.rotY - s.driftStartRotY;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        return {
          id: s.blueprint.id,
          name: s.blueprint.name,
          lateralDisplacementM: lateral,
          headingChangeDeg: Math.abs(diff) * (180 / Math.PI),
          entrySpeed: s.driftEntrySpeed,
        };
      });
    }
    // combat results are written by TestLab via setCombatResults()
  }

  setCombatResults(results: CombatResult[]): void {
    this.results.combat = results;
  }

  reset(): void {
    this.phase = "idle";
    this.elapsed = 0;
    this.waitingForInput = false;
    this.results = { timestamp: "", blueprintIds: this.states.map((s) => s.blueprint.id) };
    for (const s of this.states) {
      s.speedSubstate = "accelerating";
      s.topSpeed = 0;
      s.halfMaxTime = null;
      s.accelEndTime = null;
      s.reachedTopSpeed = false;
      s.brakeStopTime = null;
    }
  }

  start(): void {
    this.reset();
    this.beginPhase("speed");
  }

  getPhaseLabel(): string {
    if (this.waitingForInput) return "PRESS ENTER FOR NEXT TEST";
    const labels: Record<TestPhase, string> = {
      idle: "READY — PRESS ENTER TO START",
      speed: "SPEED TEST — ACCEL TO 95% MAX, THEN BRAKE",
      corner_warmup: "CORNERING — BUILD SPEED",
      corner_turn: "CORNERING — TURN UNDER THROTTLE",
      drift_warmup: "DRIFT — BUILD SPEED",
      drift_slide: "DRIFT — HANDBRAKE + STEER",
      boost: "BOOST RUN — FULL THROTTLE + BOOST",
      combat: "COMBAT — FIRING AT DUMMIES",
      done: "ALL TESTS COMPLETE",
    };
    return labels[this.phase];
  }

  getPhaseProgress(): number {
    if (this.waitingForInput) return 1;
    const dur = PHASE_DURATIONS[this.phase];
    if (!dur) return 1;
    return Math.min(1, this.elapsed / dur);
  }

  wasPhaseChanged(): boolean { return this.phaseChanged; }

  getResults(): TestResults | null {
    if (this.phase !== "done") return null;
    return this.results as TestResults;
  }

  getPartialResults(): Partial<TestResults> { return this.results; }

  // Debug helpers (used by Playwright assertions via window.__testLab)
  getStates(): ReadonlyArray<{
    id: string;
    speedSubstate: SpeedSubstate;
    topSpeed: number;
    accelEndTime: number | null;
    brakeStopTime: number | null;
    spawnX: number;
    spawnZ: number;
  }> {
    return this.states.map((s) => ({
      id: s.blueprint.id,
      speedSubstate: s.speedSubstate,
      topSpeed: s.topSpeed,
      accelEndTime: s.accelEndTime,
      brakeStopTime: s.brakeStopTime,
      spawnX: s.spawnX,
      spawnZ: s.spawnZ,
    }));
  }
}
